/**
 * © 2024 Little Shilling, Inc.
 * Shon Little
 * Created: 2024-06-19
 */

// Add third-party dependencies.
import { useState, useEffect, useCallback, useContext } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  LinearProgress,
  Box,
  Typography,
} from '@mui/material';

// Add local dependencies.
import AuthContext from '../../context/AuthContext';

/**
 * SessionTimeoutWarning component.
 * Displays a warning dialog when the user's session is about to expire.
 * @example
 * return (
 *   <SessionTimeoutWarning
 *     warningThreshold={5}
 *     logoutThreshold={1}
 *   />
 * )
 * @returns {React.ReactElement} component.
 */
const SessionTimeoutWarning = ({ warningThreshold, logoutThreshold }) => {
  // Set state hooks.
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Set context hook.
  const auth = useContext(AuthContext);

  // Safely access logout function with null check
  const logout = auth?.logout || (() => console.error('Logout function not available'));

  // Convert minutes to milliseconds
  const warningTime = warningThreshold * 60 * 1000;
  const logoutTime = logoutThreshold * 60 * 1000;
  const totalWarningTime = warningTime - logoutTime;

  // Calculate progress percentage
  const progressPercentage = ((totalWarningTime - countdown) / totalWarningTime) * 100;

  // Reset the activity timer
  const resetTimer = useCallback(() => {
    setLastActivity(Date.now());
    if (open) {
      setOpen(false);
    }
  }, [open]);

  // Handle continue session
  const handleContinue = () => {
    resetTimer();
    setOpen(false);
  };

  // Handle logout
  const handleLogout = () => {
    setOpen(false);
    logout();
  };

  // Check for user activity
  useEffect(() => {
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

    const activityHandler = () => {
      resetTimer();
    };

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, activityHandler);
    });

    // Remove event listeners on cleanup
    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, activityHandler);
      });
    };
  }, [resetTimer]);

  // Check for session timeout
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivity;

      // If warning threshold reached, show dialog
      if (timeSinceLastActivity >= warningTime - totalWarningTime && !open) {
        setOpen(true);
        setCountdown(totalWarningTime);
      }

      // If warning is showing, update countdown
      if (open) {
        // Calculate when the warning started
        const warningStartTime = lastActivity + warningTime - totalWarningTime;
        // Calculate how much time has elapsed since the warning started
        const elapsedWarningTime = now - warningStartTime;
        // Calculate the remaining warning time
        const remainingWarningTime = Math.max(0, totalWarningTime - elapsedWarningTime);
        setCountdown(remainingWarningTime);

        // If countdown reaches zero, logout
        if (remainingWarningTime <= 0) {
          handleLogout();
        }
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [lastActivity, warningTime, totalWarningTime, open, handleLogout]);

  // Format time remaining
  const formatTimeRemaining = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;

    return `${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  };

  // Render component.
  return (
    <Dialog open={open} aria-labelledby="session-timeout-title" aria-describedby="session-timeout-description">
      <DialogTitle id="session-timeout-title">Session Timeout Warning</DialogTitle>
      <DialogContent>
        <DialogContentText id="session-timeout-description">
          Your session is about to expire due to inactivity. You will be automatically logged out in:
        </DialogContentText>
        <Box sx={{ mt: 2, mb: 1 }}>
          <Typography variant="h4" align="center">
            {formatTimeRemaining(countdown)}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercentage}
          color="warning"
          sx={{ height: 10, borderRadius: 5 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout} color="secondary">
          Logout Now
        </Button>
        <Button onClick={handleContinue} color="primary" variant="contained" autoFocus>
          Continue Session
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Set component property types.
SessionTimeoutWarning.propTypes = {
  warningThreshold: PropTypes.number,
  logoutThreshold: PropTypes.number,
};

// Set component default properties.
SessionTimeoutWarning.defaultProps = {
  warningThreshold: 30, // Show warning 30 minutes before session expires
  logoutThreshold: 1, // Auto logout after 1 minute of warning
};

// Export component.
export default SessionTimeoutWarning;
